import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, fetchProjectThumbnail } from '../api.js'
import { Alert, UserSearch } from '../lib.jsx'
import { TEMPLATE_TYPES, TypeIcons, TypeLabel } from '../templateTypes.jsx'

const PAGE_SIZE = 10
const STATUS_PL = { draft: 'roboczy', content_ready: 'treść gotowa', composed: 'złożony', published: 'opublikowany' }

function Thumbnail({ project }) {
  const [src, setSrc] = useState(null)
  useEffect(() => {
    let live = true; let objectUrl = null
    if (project.thumbnail_url) fetchProjectThumbnail(project.id).then(url => { objectUrl = url; if (live) setSrc(url) })
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [project.id, project.thumbnail_url])
  return <div className="project-thumb">{src ? <img src={src} alt="Miniatura pierwszej strony" /> : <span aria-hidden="true">▧</span>}</div>
}

function SortHead({ field, sort, onSort, children }) {
  const active = sort.field === field
  return <th><button type="button" className="sort-head" onClick={() => onSort(field)}>{children} {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</button></th>
}

export default function Projects() {
  const nav = useNavigate()
  const [projects, setProjects] = useState([])
  const [templates, setTemplates] = useState([])
  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [outputType, setOutputType] = useState('')
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [showText, setShowText] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [selected, setSelected] = useState([])
  const [filters, setFilters] = useState({ name: '', status: '', template: '', type: '' })
  const [sort, setSort] = useState({ field: 'updated_at', dir: 'desc' })
  const [page, setPage] = useState(1)
  const [shareUser, setShareUser] = useState(null)
  const [shareRole, setShareRole] = useState('viewer')

  const load = () => {
    api.projects().then(setProjects).catch(e => setErr(e.message))
    api.templates().then(ts => setTemplates(ts.filter(t => t.status === 'published'))).catch(() => {})
  }
  useEffect(load, [])
  const chosenTemplate = templates.find(t => t.id === templateId)
  useEffect(() => {
    const available = chosenTemplate?.available_types || []
    setOutputType(available.includes(outputType) ? outputType : (available[0] || ''))
  }, [templateId, templates])

  const create = async (e) => {
    e.preventDefault()
    if (!templateId || !outputType) return
    setErr(''); setBusy(true)
    try {
      const project = await api.createProject({ name: name.trim() || 'Nowy projekt', template_id: templateId, output_types: [outputType] })
      if (file) {
        const fd = new FormData(); fd.append('file', file); await api.importFile(project.id, fd)
      } else if (text.trim()) {
        await api.importText(project.id, { text: text.trim(), format: 'text' })
      }
      nav(`/projects/${project.id}`)
    } catch (ex) { setErr(ex.message) } finally { setBusy(false) }
  }

  const duplicate = async (id) => {
    try { const copy = await api.duplicateProject(id); load(); nav(`/projects/${copy.id}`) }
    catch (ex) { setErr(ex.message) }
  }
  const remove = async (id, projectName) => {
    if (!window.confirm(`Usunąć projekt „${projectName}”?`)) return
    try { await api.deleteProject(id); setSelected(xs => xs.filter(x => x !== id)); load() }
    catch (ex) { setErr(ex.message) }
  }
  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`Usunąć ${selected.length} zaznaczonych projektów?`)) return
    setBusy(true); setErr('')
    try { await Promise.all(selected.map(id => api.deleteProject(id))); setSelected([]); load() }
    catch (ex) { setErr(ex.message) } finally { setBusy(false) }
  }
  const bulkShare = async () => {
    if (!selected.length || !shareUser) return
    setBusy(true); setErr('')
    try { await Promise.all(selected.map(id => api.addShare(id, shareUser.id, shareRole))); setShareUser(null); setSelected([]); load() }
    catch (ex) { setErr(ex.message) } finally { setBusy(false) }
  }

  const filtered = useMemo(() => {
    const rows = projects.filter(project => {
      const nameOk = !filters.name || (project.name || '').toLowerCase().includes(filters.name.toLowerCase())
      return nameOk && (!filters.status || project.status === filters.status) &&
        (!filters.template || project.template_id === filters.template) &&
        (!filters.type || (project.output_types || []).includes(filters.type))
    })
    return rows.sort((a, b) => {
      const av = sort.field === 'template_name' ? a.template_name : a[sort.field]
      const bv = sort.field === 'template_name' ? b.template_name : b[sort.field]
      return String(av || '').localeCompare(String(bv || ''), 'pl', { numeric: true }) * (sort.dir === 'asc' ? 1 : -1)
    })
  }, [projects, filters, sort])
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  useEffect(() => setPage(1), [filters.name, filters.status, filters.template, filters.type])
  const toggleSort = field => setSort(s => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }))
  const toggleAll = checked => setSelected(checked ? visible.filter(p => p.access_role === 'owner').map(p => p.id) : [])

  return (
    <>
      <div className="topbar"><div><h1>Ebooki / broszury PDF</h1><div className="sub">Twórz ebooki, broszury i oferty PDF z jednego, uporządkowanego przepływu.</div></div></div>
      <div className="content project-list-page">
        <Alert kind="err">{err}</Alert>
        <form className="quick-project-form" onSubmit={create}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nazwa projektu" aria-label="Nazwa projektu" />
          <label className="template-select-wrap">
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} aria-label="Szablon" required>
              <option value="">— szablon —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} · {TEMPLATE_TYPES.filter(x => (t.available_types || []).includes(x.key)).map(x => x.icon).join(' ')}</option>)}
            </select>
            {chosenTemplate && <TypeIcons compact available={chosenTemplate.available_types} />}
          </label>
          <div className="project-type-choice">
            {(chosenTemplate?.available_types || []).length <= 1 ? (
              outputType ? <TypeLabel type={outputType} /> : <span className="muted small">Typ składu</span>
            ) : (
              <select value={outputType} onChange={e => setOutputType(e.target.value)} aria-label="Typ składu">
                {(chosenTemplate.available_types || []).map(type => <option key={type} value={type}>{TEMPLATE_TYPES.find(x => x.key === type)?.icon} {TEMPLATE_TYPES.find(x => x.key === type)?.label}</option>)}
              </select>
            )}
          </div>
          <label className="quick-file"><span>{file ? file.name : 'Dodaj plik treści'}</span><input type="file" accept=".docx,.pdf,.txt,.md,.html" onChange={e => setFile(e.target.files?.[0] || null)} /></label>
          <button className="btn btn-primary" disabled={busy || !templateId || !outputType}>{busy ? 'Tworzenie…' : 'Utwórz'}</button>
          <button type="button" className="text-link quick-text-toggle" onClick={() => setShowText(v => !v)}>{showText ? 'Ukryj tekst' : 'lub wklej tekst'}</button>
          {showText && <textarea className="quick-paste" value={text} onChange={e => setText(e.target.value)} rows={4} placeholder="Wklej treść dokumentu…" />}
        </form>

        <div className="project-filters">
          <input value={filters.name} onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} placeholder="Filtruj po nazwie…" />
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}><option value="">Wszystkie statusy</option>{Object.entries(STATUS_PL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <select value={filters.template} onChange={e => setFilters(f => ({ ...f, template: e.target.value }))}><option value="">Wszystkie szablony</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}><option value="">Wszystkie typy</option>{TEMPLATE_TYPES.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}</select>
        </div>

        {selected.length > 0 && <div className="bulk-actions">
          <b>Zaznaczono: {selected.length}</b>
          <button className="btn btn-sm btn-danger" disabled={busy} onClick={bulkDelete}>Usuń zaznaczone</button>
          <div className="bulk-share"><UserSearch onPick={setShareUser} /><select value={shareRole} onChange={e => setShareRole(e.target.value)}><option value="viewer">Tylko podgląd</option><option value="editor">Edycja</option></select><button className="btn btn-sm" disabled={!shareUser || busy} onClick={bulkShare}>Udostępnij</button></div>
        </div>}

        <div className="projects-table-wrap">
          {visible.length === 0 ? <div className="empty">Brak projektów spełniających kryteria.</div> : <table className="projects-table">
            <thead><tr><th><input type="checkbox" checked={visible.some(p => p.access_role === 'owner') && visible.filter(p => p.access_role === 'owner').every(p => selected.includes(p.id))} onChange={e => toggleAll(e.target.checked)} aria-label="Zaznacz widoczne" /></th><th>Podgląd</th><SortHead field="name" sort={sort} onSort={toggleSort}>Nazwa</SortHead><SortHead field="template_name" sort={sort} onSort={toggleSort}>Szablon</SortHead><th>Typ</th><SortHead field="status" sort={sort} onSort={toggleSort}>Status</SortHead><SortHead field="updated_at" sort={sort} onSort={toggleSort}>Aktualizacja</SortHead><th>Akcje</th></tr></thead>
            <tbody>{visible.map(project => <tr key={project.id}>
              <td><input type="checkbox" disabled={project.access_role !== 'owner'} checked={selected.includes(project.id)} onChange={e => setSelected(xs => e.target.checked ? [...new Set([...xs, project.id])] : xs.filter(x => x !== project.id))} aria-label={`Zaznacz ${project.name}`} /></td>
              <td><Thumbnail project={project} /></td>
              <td><div className="project-name-cell">{project.access_role === 'viewer' && <span title="Projekt tylko do wglądu">🔒</span>}<div><button className="table-link" onClick={() => nav(`/projects/${project.id}`)}>{project.name || '(bez nazwy)'}</button>{project.is_shared && <div className="muted small">Udostępnił(a): {project.shared_by || 'użytkownik'}</div>}</div></div></td>
              <td>{project.template_name || '—'}<TypeIcons compact available={project.template_available_types || []} /></td>
              <td><TypeIcons available={project.template_available_types || []} active={project.output_types || []} /></td>
              <td><span className="pill">{STATUS_PL[project.status] || project.status}</span></td>
              <td className="muted small">{new Date(project.updated_at).toLocaleString('pl-PL')}</td>
              <td><div className="table-actions"><button className="btn btn-sm btn-ghost" onClick={() => nav(`/projects/${project.id}`)}>Edytuj</button><button className="btn btn-sm btn-ghost" onClick={() => duplicate(project.id)}>Duplikuj</button>{project.access_role === 'owner' && <button className="btn btn-sm btn-danger" onClick={() => remove(project.id, project.name)}>Usuń</button>}</div></td>
            </tr>)}</tbody>
          </table>}
        </div>
        <div className="pager"><button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Poprzednia</button><span>Strona {page} z {pageCount} · {filtered.length} projektów</span><button className="btn btn-sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Następna ›</button></div>
      </div>
    </>
  )
}
